import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaSearch, FaIndustry, FaArrowRight, FaTimes } from 'react-icons/fa';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const selectedCat = searchParams.get('kategori') || '';
  const searchTerm = searchParams.get('search') || '';

  useEffect(() => {
    axios.get('/api/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedCat) params.set('category', selectedCat);
    if (searchTerm) params.set('search', searchTerm);
    axios.get(`/api/products?${params.toString()}`).then(r => { setProducts(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [selectedCat, searchTerm]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(searchParams);
    if (slug) p.set('kategori', slug); else p.delete('kategori');
    setSearchParams(p);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (searchInput.trim()) p.set('search', searchInput.trim()); else p.delete('search');
    setSearchParams(p);
  };

  const clearSearch = () => {
    setSearchInput('');
    const p = new URLSearchParams(searchParams);
    p.delete('search');
    setSearchParams(p);
  };

  return (
    <div className="pt-20">
      {/* Page Header */}
      <div className="hero-gradient hero-pattern py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-400 mb-3">
            <Link to="/" className="hover:text-secondary">Ana Sayfa</Link> <span className="mx-2">/</span>
            <span className="text-white">Ürünler</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Ürünlerimiz</h1>
          <p className="text-gray-300">Sektörün ihtiyaçlarına yönelik geniş ürün yelpazemiz</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search + Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Ürün ara..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="input-field pl-11 pr-10"
            />
            {searchTerm && (
              <button type="button" onClick={clearSearch} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <FaTimes />
              </button>
            )}
          </form>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setCategory('')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!selectedCat ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Tümü
          </button>
          {categories.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCat === c.slug ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-sm text-gray-500 mb-6">
            {products.length} ürün bulundu
            {searchTerm && <span> — "<strong>{searchTerm}</strong>" için</span>}
          </p>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-52 bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-1/3" />
                  <div className="h-5 bg-gray-200 rounded w-2/3" />
                  <div className="h-4 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map(p => (
              <Link key={p.id} to={`/urunler/${p.slug}`} className="card group">
                <div className="h-52 img-placeholder overflow-hidden">
                  {p.main_image ? (
                    <img src={p.main_image} alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <FaIndustry size={48} />
                    </div>
                  )}
                  {p.is_featured === 1 && (
                    <span className="absolute top-3 left-3 badge bg-secondary text-white">Öne Çıkan</span>
                  )}
                </div>
                <div className="p-5">
                  {p.category_name && (
                    <span className="badge bg-secondary/10 text-secondary mb-2 inline-block">{p.category_name}</span>
                  )}
                  <h3 className="font-semibold text-primary text-lg mb-2 group-hover:text-secondary transition-colors">{p.name}</h3>
                  <p className="text-gray-500 text-sm line-clamp-2 mb-4">{p.short_description}</p>
                  <span className="text-secondary text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    Detayları Gör <FaArrowRight size={12} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <FaIndustry size={64} className="mx-auto text-gray-200 mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">Ürün Bulunamadı</h3>
            <p className="text-gray-400 text-sm">Farklı bir arama terimi deneyin veya tüm ürünleri görüntüleyin.</p>
          </div>
        )}
      </div>
    </div>
  );
}
